import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus,
    Search,
    Trash2,
    Scale,
    AlertTriangle,
    Gavel,
    FileText,
    Info,
    Calendar,
    Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { safeParseArray } from '../../utils/storageUtils';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const StaffDosimetry = () => {
    const { user } = useAuth();
    const [cards, setCards] = useState([]);
    const [search, setSearch] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        notes: '',
        minPenalty: '',
        maxPenalty: ''
    });

    useEffect(() => {
        const fetchCards = async () => {
            try {
                const { data, error } = await supabase
                    .from('system_dosimetry')
                    .select('*')
                    .order('created_at', { ascending: false });

                if (error) {
                    console.error('Error fetching dosimetry cards:', error);
                } else {
                    // Map snake_case from DB to camelCase for UI
                    const mappedData = (data || []).map(item => ({
                        id: item.id,
                        title: item.title,
                        description: item.description,
                        notes: item.notes,
                        minPenalty: item.min_penalty,
                        maxPenalty: item.max_penalty,
                        createdAt: item.created_at
                    }));
                    setCards(mappedData);
                }
            } catch (err) {
                console.error('Unexpected error loading dosimetry:', err);
            }
        };

        fetchCards();

        // Realtime subscription
        const channel = supabase.channel('system-dosimetry-realtime')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'system_dosimetry' }, fetchCards)
            .subscribe();

        return () => supabase.removeChannel(channel);
    }, []);

    const handleCreate = async () => {
        if (!formData.title || !formData.description) {
            toast.error('Preencha pelo menos o Título e a Descrição.');
            return;
        }

        try {
            const { error } = await supabase
                .from('system_dosimetry')
                .insert([{
                    title: formData.title,
                    description: formData.description,
                    notes: formData.notes,
                    min_penalty: formData.minPenalty,
                    max_penalty: formData.maxPenalty
                }]);

            if (error) throw error;

            setIsCreating(false);
            setFormData({ title: '', description: '', notes: '', minPenalty: '', maxPenalty: '' });
            toast.success('Card de Dosimetria criado com sucesso!');
        } catch (error) {
            console.error(error);
            toast.error('Erro ao criar card de dosimetria.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este card?')) {
            try {
                const { error } = await supabase
                    .from('system_dosimetry')
                    .delete()
                    .eq('id', id);

                if (error) throw error;
                toast.success('Card removido.');
            } catch (error) {
                console.error(error);
                toast.error('Erro ao excluir card.');
            }
        }
    };

    const filteredCards = cards.filter(card =>
        card.title.toLowerCase().includes(search.toLowerCase()) ||
        card.description.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Card className="bg-black/40 backdrop-blur-xl border-white/10 shadow-2xl overflow-hidden min-h-[600px]">
            <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] -z-10" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500/10 rounded-full blur-[120px] -z-10" />

            <CardHeader className="border-b border-white/5 pb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <CardTitle className="text-2xl font-bold text-white flex items-center gap-3">
                            <Scale className="h-6 w-6 text-emerald-400" />
                            Dosimetria & Punições
                        </CardTitle>
                        <CardDescription className="text-gray-400 mt-1">
                            Guia oficial de punições e regras para a equipe
                        </CardDescription>
                    </div>

                    <Dialog open={isCreating} onOpenChange={setIsCreating}>
                        <DialogTrigger asChild>
                            <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/25 border-0 rounded-xl px-6 h-11 transition-all hover:scale-105 active:scale-95">
                                <Plus className="h-5 w-5 mr-2" />
                                Criar Card
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-zinc-950 border-white/10 text-white sm:max-w-[500px]">
                            <DialogHeader>
                                <DialogTitle className="text-xl flex items-center gap-2">
                                    <FileText className="h-5 w-5 text-emerald-400" />
                                    Novo Card de Dosimetria
                                </DialogTitle>
                                <DialogDescription className="text-gray-400">
                                    Crie um novo guia de punição visível para toda a equipe.
                                </DialogDescription>
                            </DialogHeader>

                            <div className="grid gap-4 py-4">
                                <div className="space-y-2">
                                    <Label className="text-xs font-semibold uppercase text-gray-400">Título da Infração</Label>
                                    <Input
                                        placeholder="Ex: VDM (Vehicle Death Match)"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        className="bg-black/50 border-white/10 focus:border-emerald-500/50"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs font-semibold uppercase text-gray-400">Descrição / Ocorrência</Label>
                                    <Textarea
                                        placeholder="Ex: Atropelar usando o veículo..."
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="bg-black/50 border-white/10 focus:border-emerald-500/50 min-h-[80px]"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs font-semibold uppercase text-gray-400">Observações (Opcional)</Label>
                                    <Input
                                        placeholder="Ex: Se reincidente, aplicar banimento."
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                        className="bg-black/50 border-white/10 focus:border-emerald-500/50"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-xs font-semibold uppercase text-gray-400">Pena Mínima</Label>
                                        <Input
                                            placeholder="Ex: Advertência"
                                            value={formData.minPenalty}
                                            onChange={(e) => setFormData({ ...formData, minPenalty: e.target.value })}
                                            className="bg-black/50 border-white/10 focus:border-emerald-500/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-xs font-semibold uppercase text-gray-400">Pena Máxima</Label>
                                        <Input
                                            placeholder="Ex: 3 Dias de Ban"
                                            value={formData.maxPenalty}
                                            onChange={(e) => setFormData({ ...formData, maxPenalty: e.target.value })}
                                            className="bg-black/50 border-white/10 focus:border-emerald-500/50"
                                        />
                                    </div>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button variant="ghost" onClick={() => setIsCreating(false)} className="border-white/10 text-gray-400 hover:text-white hover:bg-white/5">Cancel</Button>
                                <Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white">Criar Dosimetria</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </CardHeader>

            <CardContent className="p-6 space-y-6">
                {/* Search Bar */}
                <div className="relative group max-w-xl">
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-emerald-400 transition-colors" />
                        <Input
                            placeholder="Pesquisar infrações, regras ou punições..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-12 bg-black/50 border-white/10 text-white h-12 rounded-xl focus:border-emerald-500/50 focus:ring-emerald-500/20 transition-all placeholder:text-gray-600"
                        />
                    </div>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <AnimatePresence>
                        {filteredCards.map((card) => (
                            <motion.div
                                key={card.id}
                                initial={{ opacity: 0, scale: 0.98 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="group relative bg-white/5 hover:bg-white/[0.07] border border-white/5 hover:border-emerald-500/30 rounded-xl p-5 transition-all duration-300 flex flex-col gap-4"
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                                            <Gavel className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-white group-hover:text-emerald-400 transition-colors">
                                                {card.title}
                                            </h3>
                                        </div>
                                    </div>
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        onClick={() => handleDelete(card.id)}
                                        className="h-8 w-8 text-gray-500 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>

                                <div className="space-y-3">
                                    <div className="bg-black/20 rounded-lg p-3 border border-white/5">
                                        <h4 className="text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1.5 uppercase tracking-wider">
                                            <Info className="h-3 w-3" /> Descrição
                                        </h4>
                                        <p className="text-gray-300 text-sm leading-relaxed">
                                            {card.description}
                                        </p>
                                    </div>

                                    {card.notes && (
                                        <div className="flex items-start gap-2 text-sm text-yellow-500/90 bg-yellow-500/5 p-2 rounded-lg border border-yellow-500/10">
                                            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                                            <span><span className="font-semibold">Obs:</span> {card.notes}</span>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-3 pt-2">
                                        <div className="space-y-1">
                                            <span className="text-xs text-gray-500 uppercase font-bold tracking-wider flex items-center gap-1.5">
                                                <Scale className="h-3 w-3" /> Pena Mínima
                                            </span>
                                            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 w-full justify-center py-1">
                                                {card.minPenalty || "N/A"}
                                            </Badge>
                                        </div>
                                        <div className="space-y-1">
                                            <span className="text-xs text-gray-500 uppercase font-bold tracking-wider flex items-center gap-1.5">
                                                <Gavel className="h-3 w-3" /> Pena Máxima
                                            </span>
                                            <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/20 w-full justify-center py-1">
                                                {card.maxPenalty || "N/A"}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {filteredCards.length === 0 && (
                        <div className="col-span-full py-12 text-center flex flex-col items-center justify-center opacity-50">
                            <Scale className="h-12 w-12 text-gray-600 mb-4" />
                            <p className="text-gray-400 text-lg">Nenhum registro de dosimetria encontrado.</p>
                            {search && <p className="text-gray-600 text-sm mt-1">Tente buscar por outros termos.</p>}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default StaffDosimetry;
